import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, Loader2, GraduationCap, 
  ChevronDown, ChevronUp, Pen, Shield, AlertTriangle
} from 'lucide-react';
import api from '../lib/api';

const TutorAgreementPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreementData, setAgreementData] = useState(null);
  const [existingAgreement, setExistingAgreement] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [acknowledgments, setAcknowledgments] = useState({});
  const [signature, setSignature] = useState('');
  const [allSectionsRead, setAllSectionsRead] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    loadAgreement();
  }, []);

  const loadAgreement = async () => {
    try {
      // Get agreement content
      const agreementRes = await api.get('/tutor/agreement');
      setAgreementData(agreementRes.data);
      
      // Initialize acknowledgments and expanded state
      const sections = Object.keys(agreementRes.data.sections);
      const initialAck = {};
      const initialExpanded = {};
      sections.forEach(s => {
        initialAck[s] = false;
        initialExpanded[s] = false;
      });
      setAcknowledgments(initialAck);
      setExpandedSections(initialExpanded);
      
      // Check if already signed
      const statusRes = await api.get('/tutor/agreement/status');
      if (statusRes.data.signed) {
        setExistingAgreement(statusRes.data.agreement);
      }
    } catch (error) {
      console.error('Error loading agreement:', error);
      toast.error('Failed to load agreement');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleAcknowledge = (sectionKey, checked) => {
    setAcknowledgments(prev => ({
      ...prev,
      [sectionKey]: checked
    }));
  };

  const allAcknowledged = agreementData ? 
    Object.keys(agreementData.sections).every(key => acknowledgments[key]) : false;

  const handleSubmit = async () => {
    if (!allAcknowledged) {
      toast.error('Please acknowledge all sections before signing');
      return;
    }
    if (!signature.trim() || signature.trim().length < 3) {
      toast.error('Please enter your full legal name as signature');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/tutor/agreement/sign', {
        full_legal_name: signature.trim(),
        acknowledgments
      });
      
      toast.success('Agreement signed successfully!');
      if (response.data.email_sent) {
        toast.success('A copy of the agreement has been sent to your email');
      }
      
      // Redirect to document upload
      navigate('/tutor/documents');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign agreement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Already signed - show status
  if (existingAgreement) {
    return (
      <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="agreement-signed-page">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-card">
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2">Agreement Already Signed</h2>
              <p className="text-slate-600 mb-6">
                You signed the Tutor Agreement on{' '}
                <span className="font-medium">
                  {new Date(existingAgreement.signed_at).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </span>
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-slate-600">
                  <strong>Version:</strong> {existingAgreement.agreement_version}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Signature:</strong> <span className="font-cursive italic">{existingAgreement.typed_signature}</span>
                </p>
              </div>
              <Button onClick={() => navigate('/tutor/documents')} className="rounded-full">
                Continue to Document Upload
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="tutor-agreement-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <GraduationCap className="h-10 w-10 text-indigo-700" />
            <span className="font-heading text-2xl font-semibold text-slate-900">
              Gifted Academy Tutors
            </span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Tutor Agreement</h1>
          <p className="text-slate-600">Please read and acknowledge each section before signing</p>
          <Badge className="mt-2 bg-indigo-100 text-indigo-700">
            Version {agreementData?.version}
          </Badge>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
            <span>Sections Acknowledged</span>
            <span>
              {Object.values(acknowledgments).filter(Boolean).length} / {Object.keys(agreementData?.sections || {}).length}
            </span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-indigo-600 transition-all duration-300"
              style={{ 
                width: `${(Object.values(acknowledgments).filter(Boolean).length / Object.keys(agreementData?.sections || {}).length) * 100}%` 
              }}
            />
          </div>
        </div>

        {/* Agreement Sections */}
        <div className="space-y-4 mb-8">
          {agreementData && Object.entries(agreementData.sections).map(([key, section], index) => (
            <Card 
              key={key} 
              className={`transition-all ${acknowledgments[key] ? 'border-green-200 bg-green-50/30' : ''}`}
              data-testid={`agreement-section-${key}`}
            >
              <CardHeader 
                className="cursor-pointer"
                onClick={() => toggleSection(key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      acknowledgments[key] ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {acknowledgments[key] ? <CheckCircle className="h-5 w-5" /> : index + 1}
                    </div>
                    <CardTitle className="text-lg">{section.title}</CardTitle>
                  </div>
                  {expandedSections[key] ? (
                    <ChevronUp className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  )}
                </div>
              </CardHeader>
              
              {expandedSections[key] && (
                <CardContent className="pt-0">
                  <div className="bg-white rounded-lg p-4 border mb-4 max-h-[300px] overflow-y-auto">
                    <p className="text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id={`ack-${key}`}
                      checked={acknowledgments[key]}
                      onCheckedChange={(checked) => handleAcknowledge(key, checked)}
                      data-testid={`ack-checkbox-${key}`}
                    />
                    <Label 
                      htmlFor={`ack-${key}`}
                      className="text-sm text-slate-700 cursor-pointer"
                    >
                      I have read and understood the "{section.title}" section
                    </Label>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Signature Section */}
        <Card className="mb-8" data-testid="signature-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <Pen className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle>Digital Signature</CardTitle>
                <CardDescription>Type your full legal name to sign this agreement</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!allAcknowledged && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Please read and acknowledge all sections above before signing.
                </p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="signature">Full Legal Name</Label>
                <Input
                  id="signature"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Enter your full legal name"
                  className="text-lg"
                  disabled={!allAcknowledged}
                  data-testid="signature-input"
                />
              </div>
              
              {signature && (
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-slate-500 mb-2">Preview:</p>
                  <p className="text-2xl font-cursive italic text-slate-700">{signature}</p>
                </div>
              )}
              
              <div className="text-xs text-slate-500">
                <Shield className="h-4 w-4 inline mr-1" />
                By signing, you agree to all terms and conditions outlined in this agreement. 
                A copy will be emailed to you for your records.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={!allAcknowledged || !signature.trim() || submitting}
            className="rounded-full px-8"
            data-testid="sign-agreement-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Signing Agreement...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-5 w-5" />
                Sign Agreement
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TutorAgreementPage;
